import { useState, useEffect, useCallback } from 'react';
import { CallRoute, CreateRouteData, UpdateRouteData } from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiError, ProcessedErrors } from '@utils/errorHandler';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useCallRoutes = () => {
  const [callRoutes, setCallRoutes] = useState<CallRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCallRoutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiClient.get('/telephony/call-routes/');
      if (response.ok) {
        const data: CallRoute[] = await response.json();
        setCallRoutes(data);
      } else {
        const processedErrors = await handleApiError(response);
        setError(processedErrors.generalError || 'Failed to fetch call routes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch call routes');
    } finally {
      setLoading(false);
    }
  }, []);

  const createCallRoute = async (
    data: CreateRouteData
  ): Promise<{
    success: boolean;
    callRoute?: CallRoute;
    errors?: ProcessedErrors;
  }> => {
    try {
      const response = await ApiClient.post('/telephony/call-routes/', data);
      const result = await handleApiResult<CallRoute>(response, 'Failed to create call route');

      if (result.success && result.data) {
        setCallRoutes(prev => [...prev, result.data!]);
        return { success: true, callRoute: result.data };
      }

      return { success: false, errors: result.errors };
    } catch (err) {
      return {
        success: false,
        errors: handleApiCatch(err, 'Failed to create call route'),
      };
    }
  };

  const updateCallRoute = async (
    id: string,
    data: UpdateRouteData
  ): Promise<{
    success: boolean;
    callRoute?: CallRoute;
    errors?: ProcessedErrors;
  }> => {
    try {
      const response = await ApiClient.patch(`/telephony/call-routes/${id}/`, data);
      const result = await handleApiResult<CallRoute>(response, 'Failed to update call route');

      if (result.success && result.data) {
        setCallRoutes(prev => prev.map(route => (route.id === id ? result.data! : route)));
        return { success: true, callRoute: result.data };
      }

      return { success: false, errors: result.errors };
    } catch (err) {
      return {
        success: false,
        errors: handleApiCatch(err, 'Failed to update call route'),
      };
    }
  };

  const deleteCallRoute = async (id: string): Promise<boolean> => {
    try {
      const response = await ApiClient.delete(`/telephony/call-routes/${id}/`);

      if (response.ok) {
        setCallRoutes(prev => prev.filter(route => route.id !== id));
        return true;
      } else {
        const processedErrors = await handleApiError(response);
        setError(processedErrors.generalError || 'Failed to delete call route');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete call route');
      return false;
    }
  };

  const fetchCallRoute = async (id: string): Promise<CallRoute | null> => {
    try {
      const response = await ApiClient.get(`/telephony/call-routes/${id}/`);
      if (response.ok) {
        return await response.json();
      } else {
        const processedErrors = await handleApiError(response);
        setError(processedErrors.generalError || 'Failed to fetch call route');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch call route');
      return null;
    }
  };

  useEffect(() => {
    fetchCallRoutes();
  }, [fetchCallRoutes]);

  return {
    callRoutes,
    loading,
    error,
    fetchCallRoutes,
    createCallRoute,
    updateCallRoute,
    deleteCallRoute,
    fetchCallRoute,
  };
};
